import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { imageService } from '../services/api';

type DbImage = {
  id: number | string;
  title: string;
  description?: string;
  url: string;
  category?: string;
  date_publication?: string;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://aikash.ci';

const buildMediaUrl = (url?: string): string => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/uploads/')) return encodeURI(`${API_BASE_URL}${url}`);
  return url;
};

// Composants stylisés
const MainContent = styled.main`
  flex: 1;
  margin-top: 80px;
  min-height: calc(100vh - 80px);
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, rgba(30, 64, 175, 0.8) 0%, rgba(59, 130, 246, 0.8) 100%);
  color: white;
  padding: 80px 20px;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PhotosSection = styled.section`
  padding: 80px 0;
  background: white;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 50px;
  font-weight: 700;
`;

const CategoryFilter = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 40px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 12px 24px;
  border: 2px solid ${props => props.active ? '#1e40af' : '#e1e5e9'};
  border-radius: 25px;
  background: ${props => props.active ? '#1e40af' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }
`;

const PhotosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 50px;
`;

const PhotoCard = styled.div`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const PhotoImage = styled.div<{ imageUrl: string }>`
  width: 100%;
  height: 250px;
  background: url(${props => props.imageUrl});
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.2);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

const PhotoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(30, 64, 175, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${PhotoCard}:hover & {
    opacity: 1;
  }
`;

const ViewButton = styled.button`
  background: white;
  color: #1e40af;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  }
`;

const PhotoContent = styled.div`
  padding: 20px;
`;

const PhotoTitle = styled.h3`
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 10px;
  font-weight: 600;
`;

const PhotoDescription = styled.p`
  color: #666;
  line-height: 1.5;
  font-size: 0.9rem;
  margin-bottom: 15px;
`;

const PhotoMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #888;
  font-size: 0.8rem;
`;

const PhotoDate = styled.span`
  font-style: italic;
`;

const PhotoCategory = styled.span`
  background: #f8f9fa;
  padding: 4px 8px;
  border-radius: 10px;
  font-weight: 500;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px;
  font-size: 1.2rem;
  color: #6b7280;
`;

const PaginationRow = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid ${p => (p.active ? '#1e40af' : '#e5e7eb')};
  background: ${p => (p.active ? '#1e40af' : '#fff')};
  color: ${p => (p.active ? '#fff' : '#111827')};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 44px;

  &:hover {
    background: ${p => (p.active ? '#1e40af' : '#f3f4f6')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PhotoModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 9999;
`;

const PhotoModalContent = styled.div`
  width: min(980px, 100%);
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 70px rgba(0,0,0,0.35);
`;

const PhotoModalHeader = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
`;

const PhotoModalTitle = styled.div`
  font-weight: 800;
  color: #111827;
  font-size: 1.05rem;
  line-height: 1.3;
`;

const PhotoModalSubtitle = styled.div`
  margin-top: 4px;
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.4;
  max-width: 780px;
`;

const PhotoModalCloseButton = styled.button`
  border: none;
  background: #f3f4f6;
  color: #111827;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: #e5e7eb;
  }
`;

const PhotoModalImage = styled.img`
  width: 100%;
  height: auto;
  max-height: calc(100vh - 180px);
  object-fit: contain;
  background: #000;
  display: block;
`;

const StatsSection = styled.section`
  padding: 60px 0;
  background: #f8f9fa;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 40px;
  margin-top: 40px;
`;

const StatCard = styled.div`
  text-align: center;
  padding: 30px 20px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e40af;
  margin-bottom: 10px;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: #666;
  font-weight: 500;
`;

function PhotosPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [photos, setPhotos] = useState<DbImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [activePhoto, setActivePhoto] = useState<DbImage | null>(null);

  const pageSize = 9;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await imageService.getAllImages();
        setPhotos(data || []);
      } catch (e) {
        console.error('Erreur lors du chargement des photos:', e);
        setPhotos([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const categories = useMemo(() => {
    const unique = Array.from(new Set(photos.map(p => (p.category || 'Événements')).filter(Boolean)));
    return [{ id: 'all', name: 'Toutes les photos' }, ...unique.map(c => ({ id: c, name: c }))];
  }, [photos]);

  const filteredPhotos = useMemo(() => {
    const list = selectedCategory === 'all'
      ? photos
      : photos.filter(p => (p.category || 'Événements') === selectedCategory);
    return list;
  }, [photos, selectedCategory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredPhotos.length / pageSize));
  const paginatedPhotos = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredPhotos.slice(start, start + pageSize);
  }, [filteredPhotos, currentPage]);

  const goToPage = (p: number) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setCurrentPage(next);
  };

  return (
    <MainContent>
      <HeroSection>
        <Container>
          <HeroTitle>📸 Photos Karaté Shotokan</HeroTitle>
          <HeroSubtitle>
            Explorez notre galerie de photos : compétitions, entraînements, 
            cérémonies et moments inoubliables du karaté traditionnel
          </HeroSubtitle>
        </Container>
      </HeroSection>

      <PhotosSection>
        <Container>
          <SectionTitle>Notre Galerie Photo</SectionTitle>
          
          <CategoryFilter>
            {categories.map(category => (
              <FilterButton
                key={category.id}
                active={selectedCategory === category.id}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </FilterButton>
            ))}
          </CategoryFilter>

          {loading ? (
            <LoadingSpinner>⏳ Chargement des photos...</LoadingSpinner>
          ) : (
            <>
              <PhotosGrid>
                {paginatedPhotos.map(photo => (
                  <PhotoCard key={photo.id} onClick={() => setActivePhoto(photo)}>
                    <PhotoImage imageUrl={buildMediaUrl(photo.url) || '/karate1.webp'}>
                      <PhotoOverlay>
                        <ViewButton>Voir la photo</ViewButton>
                      </PhotoOverlay>
                    </PhotoImage>
                    <PhotoContent>
                      <PhotoTitle>{photo.title}</PhotoTitle>
                      <PhotoDescription>{photo.description}</PhotoDescription>
                      <PhotoMeta>
                        <PhotoDate>
                          {photo.date_publication
                            ? new Date(photo.date_publication).toLocaleDateString('fr-FR')
                            : ''}
                        </PhotoDate>
                        <PhotoCategory>{photo.category || 'Événements'}</PhotoCategory>
                      </PhotoMeta>
                    </PhotoContent>
                  </PhotoCard>
                ))}
              </PhotosGrid>

              {filteredPhotos.length > pageSize && (
                <PaginationRow>
                  <PageButton disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>
                    ‹
                  </PageButton>
                  {Array.from({ length: totalPages }).slice(0, 10).map((_, idx) => {
                    const page = idx + 1;
                    return (
                      <PageButton
                        key={page}
                        active={page === currentPage}
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </PageButton>
                    );
                  })}
                  <PageButton disabled={currentPage >= totalPages} onClick={() => goToPage(currentPage + 1)}>
                    ›
                  </PageButton>
                </PaginationRow>
              )}
            </>
          )}
        </Container>
      </PhotosSection>

      <StatsSection>
        <Container>
          <SectionTitle>Statistiques de la Galerie</SectionTitle>
          <StatsGrid>
            <StatCard>
              <StatNumber>{photos.length}</StatNumber>
              <StatLabel>Photos Disponibles</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{Math.max(0, categories.length - 1)}</StatNumber>
              <StatLabel>Catégories</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>2024</StatNumber>
              <StatLabel>Année de Création</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>HD</StatNumber>
              <StatLabel>Qualité d'Image</StatLabel>
            </StatCard>
          </StatsGrid>
        </Container>
      </StatsSection>

      {activePhoto && (
        <PhotoModalOverlay role="dialog" aria-modal="true" onClick={() => setActivePhoto(null)}>
          <PhotoModalContent onClick={(e) => e.stopPropagation()}>
            <PhotoModalHeader>
              <div>
                <PhotoModalTitle>{activePhoto.title}</PhotoModalTitle>
                {activePhoto.description && (
                  <PhotoModalSubtitle>{activePhoto.description}</PhotoModalSubtitle>
                )}
              </div>
              <PhotoModalCloseButton onClick={() => setActivePhoto(null)} aria-label="Fermer">
                ✕
              </PhotoModalCloseButton>
            </PhotoModalHeader>
            <PhotoModalImage
              src={buildMediaUrl(activePhoto.url) || '/karate1.webp'}
              alt={activePhoto.title}
              loading="lazy"
              onError={(e) => {
                const img = e.currentTarget;
                img.onerror = null;
                img.src = '/karate1.webp';
              }}
            />
          </PhotoModalContent>
        </PhotoModalOverlay>
      )}
    </MainContent>
  );
}

export default PhotosPage;
